import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Platform } from '@ionic/angular/lazy';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
  timer,
  Observable,
  defer,
} from 'rxjs';
import { InterPonctuelleForResponsableDTO } from '../entities/demande/inter-ponctuelle-for-responsable.dto';
import { NotificationInteractionService } from '../notification-alerte/domain/notification-interaction.service';
import { AuthService } from './authentication.service';
import { DemandesService } from './demandes.service';

@Injectable({
  providedIn: 'root',
})
export class DemandesATraiterService {
  private readonly platform = inject(Platform);
  private readonly authService = inject(AuthService);
  private readonly demandesService = inject(DemandesService);
  private readonly notificationInteractionService = inject(NotificationInteractionService);

  private readonly refreshTrigger$ = new Subject<any>();

  /**
   * Centralized Role check:
   * We merge the initial value from getUser with the stream of changes.
   * We avoid switchMap(getUser) to prevent the infinite loop.
   */
  public readonly isResponsable$ = merge(
    defer(() => from(this.authService.getUser())),
    this.authService.onUserChange()
  ).pipe(
    map(user => user?.authorities?.includes(AuthService.ROLE_RESPONSABLE) ?? false),
    distinctUntilChanged(),
    shareReplay(1)
  );

  // Active state based on platform lifecycle
  private readonly isActive$ = merge(this.platform.resume.pipe(map(() => true)), this.platform.pause.pipe(map(() => false)), of(true)).pipe(
    distinctUntilChanged(),
    shareReplay(1)
  );

  private readonly data$: Observable<InterPonctuelleForResponsableDTO[]> = combineLatest([this.isResponsable$, this.isActive$]).pipe(
    switchMap(([isResp, active]) => {
      if (!isResp || !active) {
        return this.refreshTrigger$.pipe(
          tap(event => event?.target?.complete?.()),
          map(() => [] as InterPonctuelleForResponsableDTO[]),
          startWith([] as InterPonctuelleForResponsableDTO[])
        );
      }

      return merge(timer(0, 60000), this.refreshTrigger$, this.notificationInteractionService.notificationReceived$.pipe(map(() => null))).pipe(
        switchMap(event =>
          this.demandesService.getDemandesATraiter().pipe(
            map(demandes => demandes.map(InterPonctuelleForResponsableDTO.hydrate)),
            catchError(() => of([])),
            tap(() => event?.target?.complete?.())
          )
        )
      );
    }),
    shareReplay(1)
  );

  public readonly demandes = toSignal(this.data$, { initialValue: [] as InterPonctuelleForResponsableDTO[] });
  public readonly count = toSignal(this.data$.pipe(map(d => d.length)), { initialValue: 0 });

  public refresh(event?: any): void {
    this.refreshTrigger$.next(event);
  }
}
